import { Suspense, lazy, useState, useEffect, useRef } from 'react'
import { useMutation } from 'react-query'
import { useLayout } from '@hooks/useLayout'
import { useRouterTransition } from '@hooks/useRouterTransition'
import { BrowserRouter as AppRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { DefaultRoute, Routes as AppRoutes } from './routes'
import { App as CapApp } from '@capacitor/app'
import { NotifContext } from '@src/utility/context/NotifContext'
import { EmbedContext } from '@src/utility/context/EmbedContext'
import { saveNotifToken } from '@src/api/profile'
import { Capacitor } from '@capacitor/core'
import { Device } from '@capacitor/device'
import { PushNotifications } from '@capacitor/push-notifications'
import { getInternalOrg, setInternalOrg, clearInternalOrg } from '@utils'
import useAuthStatus from '@hooks/useAuthStatus'
import Spinner from '@src/@core/components/spinner/Fallback-spinner'
import BlankLayout from '@layouts/BlankLayout'
import LayoutWrapper from '@layouts/components/layout-wrapper'
import VerticalLayout from '@src/layouts/VerticalLayout'
import HorizontalLayout from '@src/layouts/HorizontalLayout'
import OrgRedirect from '@comps/OrgRedirect'

const NotAuthorized = lazy(() => import('@src/views/general/NotAuthorized'))
const Error = lazy(() => import('@src/views/general/Error'))

const FinalRoute = ({ route }) => {
  const location = useLocation()
  const { loggedIn, checkLogin } = useAuthStatus()

  if (checkLogin) return <Suspense fallback={<Spinner type='grow' color='primary'> </Spinner>}>
    <Spinner type='grow' color='primary'> </Spinner>
  </Suspense>

  if (!loggedIn && (!route.meta?.authRoute && !route.meta?.publicRoute)) return <Navigate to='/login' state={{ from: location }} replace />
  else if (route.meta?.authRoute && loggedIn) return <Navigate to={DefaultRoute} replace />
  else {
    const Component = route.component
    return <Suspense fallback={<Spinner type='grow' color='primary'> </Spinner>}>
      <Component />
    </Suspense>
  }
}

const useDeepLinks = () => {
  const navigate = useNavigate()

  useEffect(() => {
    CapApp.addListener('appUrlOpen', (event) => {
      const url = new URL(event.url)
      navigate(url.pathname + url.search)
    })
  }, [navigate])
}

const usePushNotifs = () => {
  const navigate = useNavigate()
  const { loggedIn, checkLogin } = useAuthStatus()
  const [token, setToken] = useState(null)
  const [deviceId, setDeviceId] = useState(null)
  const [deviceName, setDeviceName] = useState(null)
  const listenersAttached = useRef(false)

  const fcmMutation = useMutation((data) => saveNotifToken(data), {
    onError: (data) => { console.log('oops: ', data) },
    onSuccess: (rData) => { console.log('~ fcmMutation  saveNotifToken:', JSON.stringify(rData?.data)) }
  })

  const saveToken = fcm => {
    console.log('~ saveToken ~ :', deviceId, deviceName, fcm)
    fcmMutation.mutate({ deviceId, deviceName, token: fcm })
  }

  useEffect(() => {
    if (listenersAttached.current || !Capacitor.isNativePlatform()) return
    const doStuff = async () => {
      try {
        listenersAttached.current = true
        console.log('Push attach listeners')
        await PushNotifications.addListener('registration', (token) => setToken(token.value))
        await PushNotifications.addListener('registrationError', (err) => { console.error('Push registration error:', err) })
        await PushNotifications.addListener('pushNotificationReceived', (action) => { console.log('~ PushNotification Received ~ action:', JSON.stringify(action)) })
        await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
          console.log('~ PushNotifications ActionPerformed ~ action:', action)
          const data = action.notification.data
          if (data.route) navigate(data.route)
        })
        window.addEventListener('fcmToken', (event) => {
          console.log('fcmToken:', event?.detail)
          setToken(event?.detail)
        })
      } catch (err) { console.error('Error during push attach listeners:', err) }
    }
    doStuff()
  }, [navigate])

  useEffect(() => {
    const initPush = async () => {
      console.log('Starting initPush...')
      try {
        const id = await Device.getId()
        const info = await Device.getInfo()
        setDeviceId(id.identifier)
        setDeviceName(info.name || `${info.platform}-${info.operatingSystem}`)
        const permStatus = await PushNotifications.requestPermissions()
        if (permStatus.receive === 'granted') await PushNotifications.register()
      } catch (err) { console.error('Error during initPush:', err) }
    }
    if (loggedIn && !checkLogin && Capacitor.isNativePlatform()) initPush()
  }, [loggedIn, checkLogin])

  useEffect(() => { if (loggedIn && token && deviceId && deviceName) saveToken(token) }, [loggedIn, token, deviceId, deviceName])
  return { token, saveToken }
}

const DeepLinkHandler = () => {
  useDeepLinks()
  return null
}

const NotifHandler = ({ children }) => {
  const { token, saveToken } = usePushNotifs()

  return (
    <NotifContext.Provider value={{ token, saveToken }}>
      {children}
    </NotifContext.Provider>
  )
}

const EmbedHandler = ({ children }) => {
  const location = useLocation()
  const [isEmbed, setIsEmbed] = useState(false)
  const [internalOrgId, setInternalOrgIdState] = useState(null)

  useEffect(() => {
    const iOrg = getInternalOrg()

    if (iOrg) {
      setIsEmbed(true)
      setInternalOrgIdState(iOrg)
    } else {
      setIsEmbed(false)
      setInternalOrgIdState(null)
    }
  }, [location])

  const setInternalOrgId = id => {
    setInternalOrgIdState(id)
    if (id) {
      setInternalOrg(id)
      setIsEmbed(true)
    } else {
      clearInternalOrg()
      setIsEmbed(false)
    }
  }

  const embedOrg = oId => (!isEmbed || oId === internalOrgId)

  return (
    <EmbedContext.Provider value={{ isEmbed, internalOrgId, setInternalOrgId, embedOrg }}>
      {children}
    </EmbedContext.Provider>
  )
}

const Router = () => {
  const [layout, setLayout] = useLayout()
  const [transition, setTransition] = useRouterTransition()
  const currentActiveItem = null
  const Layouts = { BlankLayout, VerticalLayout, HorizontalLayout }
  const getRoutesForLayout = ln => AppRoutes.filter(r => (r.layout === ln || (r.layout === undefined && ln === (layout === 'horizontal' ? 'HorizontalLayout' : 'VerticalLayout'))))

  const renderRoutes = (layoutKey, LayoutComponent) => {
    const routes = getRoutesForLayout(layoutKey)

    return routes.map(route => {
      const Wrapper = <LayoutComponent
        layout={layoutKey}
        setLayout={setLayout}
        transition={transition}
        setTransition={setTransition}
        currentActiveItem={currentActiveItem}>
        <Suspense fallback={<Spinner type='grow' color='primary'> </Spinner>}>
          <LayoutWrapper
            layout={layout}
            transition={transition}
            setTransition={setTransition}
            {...(route.appLayout ? { appLayout: route.appLayout } : {})}
            {...(route.meta ? { routeMeta: route.meta } : {})}
            {...(route.className ? { wrapperClass: route.className } : {})}>
            <FinalRoute route={route} />
          </LayoutWrapper>
        </Suspense>
      </LayoutComponent>

      return <Route key={route.path} path={route.path} element={Wrapper} />
    })
  }

  return <AppRouter basename={process.env.REACT_APP_BASENAME}>
    <DeepLinkHandler />
    <NotifHandler>
      <EmbedHandler>
        <Routes>
          <Route path='/' element={<OrgRedirect showSpinner={true} />} />

          <Route path='/not-authorized' element={<BlankLayout>
            <NotAuthorized />
          </BlankLayout>} />

          {Object.entries(Layouts).flatMap(([layoutKey, LayoutComponent]) => renderRoutes(layoutKey, LayoutComponent))}

          <Route path='*' element={<Error />} />
        </Routes>
      </EmbedHandler>
    </NotifHandler>
  </AppRouter>
}

export default Router